import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n/i18n';

/**
 * Compact language switcher used in the site header. Renders the
 * active language's 2-letter short code; opens a popover with the
 * full list on click. Falls back to English-style 2-letter codes
 * (EN/NL/FR/PT) so the header stays tight on mobile.
 */
export interface LanguageSwitcherProps {
  /**
   * - `icon`   (default) globe + code, ghost style
   * - `pill`   outline "EN" pill — redesign top nav (48×40 desktop, 34 mobile)
   * - `inline` footer row "NL FR EN PT ES"; active one bold. No popover.
   */
  variant?: 'icon' | 'pill' | 'inline';
}

export function LanguageSwitcher({ variant = 'icon' }: LanguageSwitcherProps = {}) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Find the active language; map pt-PT/pt to pt-BR for label.
  const currentCode = i18n.resolvedLanguage || i18n.language || 'en';
  const active = SUPPORTED_LANGUAGES.find((l) =>
    currentCode === l.code || currentCode.startsWith(l.code + '-') || (l.code === 'pt-BR' && currentCode.startsWith('pt'))
  ) ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (variant === 'inline') {
    return (
      <ul aria-label={t('language.switcherLabel')} className="flex items-center gap-3">
        {SUPPORTED_LANGUAGES.map((l) => {
          const isActive = l.code === active.code;
          return (
            <li key={l.code}>
              <button
                type="button"
                lang={l.code}
                title={l.label}
                aria-pressed={isActive}
                onClick={() => i18n.changeLanguage(l.code)}
                className={[
                  'hit-44 rounded px-1.5 py-1 text-[12.5px] transition-colors',
                  isActive
                    ? 'font-semibold text-sl-on-dark-chip'
                    : 'font-normal text-sl-on-dark-faint hover:text-sl-on-dark-chip',
                ].join(' ')}
              >
                {l.short}
                <span className="sr-only"> {l.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`${t('language.switcherLabel')}: ${active.short}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={
          variant === 'pill'
            ? 'hit-44 inline-flex h-[34px] min-w-[44px] items-center justify-center rounded-full border border-sl-line-strong bg-transparent px-3 text-[13px] font-semibold text-sl-ink transition-colors hover:bg-sl-surface md:h-ctl-sm md:min-w-[48px] md:text-[14px]'
            : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors'
        }
      >
        {variant === 'icon' && <Globe className="w-4 h-4" />}
        <span className={variant === 'icon' ? 'font-semibold text-xs' : undefined}>{active.short}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full mt-2 min-w-[180px] rounded-xl border border-border bg-card shadow-lg overflow-hidden z-50"
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const isActive = l.code === active.code;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(l.code);
                    setOpen(false);
                  }}
                  className={[
                    'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-foreground/[0.04] text-foreground'
                      : 'text-foreground hover:bg-secondary',
                  ].join(' ')}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground w-6">{l.short}</span>
                    <span className="font-medium">{l.label}</span>
                  </span>
                  {isActive && <Check className="w-4 h-4 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
